import { useState } from 'react';

import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import calendar from 'dayjs/plugin/calendar';

import { listUserActivityOptions } from '@drivingdoc/api-client/generated/react-query';
import { UserActivityItem } from 'types/userActivity';

dayjs.extend(calendar);
dayjs.extend(advancedFormat);

const columns: GridColDef<UserActivityItem>[] = [
  {
    field: 'description',
    headerName: 'Activity Description',
    type: 'string',
    sortable: false,
    width: 500,
  },
  {
    field: 'timestamp',
    headerName: 'Time',
    type: 'dateTime',
    sortable: true,
    width: 250,
    valueFormatter: (value) => {
      if (value) {
        return dayjs(value).calendar(null, {
          sameElse: 'MMM D, YYYY [at] h:mm a',
        });
      }
      return null;
    },
  },
  {
    field: 'ip_address',
    headerName: 'IP Address',
    type: 'string',
    sortable: false,
    width: 250,
  },
  {
    field: 'user_agent',
    headerName: 'User Agent',
    type: 'string',
    sortable: false,
    flex: 1,
  },
];

interface UserActivityProps {
  userId: string;
}

export function UserActivity({ userId: id }: UserActivityProps) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0,
  });

  const userActivity = useQuery({
    ...listUserActivityOptions({
      path: {
        id: id ?? '',
      },
      query: {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      },
    }),
    // use previous data as placeholder to maintain a stable
    // reference while fetching the next page so the datagrid
    // doesn't reset the pagination model
    placeholderData: keepPreviousData,
    enabled: !!id,
  });
  const activityData = userActivity.data as { data: UserActivityItem[]; total: number } | undefined;

  return (
    <DataGrid<UserActivityItem>
      loading={userActivity.isFetching}
      columns={columns}
      rows={activityData?.data}
      paginationMode="server"
      rowCount={activityData?.total ?? 0}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      disableColumnFilter
      disableColumnSorting
      sx={{
        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
          outline: 'none',
        },
        '& .MuiInputBase-root': {
          width: 'auto!important',
        },
      }}
    />
  );
}

export default UserActivity;
